import { loadChapter } from "@/features/reader";
import type { PowerSyncDrizzleDB } from "@bso/powersync";
import type { components } from "@bso/client";

type VerseResponse = components["schemas"]["VerseResponse"];

const chapterCache = new Map<string, Promise<VerseResponse[]>>();

/**
 * Fetches the verses of a chapter, memoising in-flight and resolved requests
 * so many consumers (e.g. highlight cards) share one request per chapter.
 *
 * OFFLINE-FIRST (CC-11): resolves through `loadChapter`, which reads the local
 * PowerSync base (`bible_verse_content`) first, then the Tauri native command,
 * and only falls back to the HTTP API as a last resort. Passing the local `db`
 * lets views like `/highlights` render verse snippets fully offline instead of
 * crashing on a failed network request.
 */
export async function fetchChapterVerses(
  db: PowerSyncDrizzleDB | null,
  version: string,
  book: number,
  chapter: number,
): Promise<VerseResponse[]> {
  const key = `${version.toLowerCase()}-${book}-${chapter}`;
  let promise = chapterCache.get(key);
  if (!promise) {
    promise = (async () => {
      try {
        return await loadChapter(db, version.toLowerCase(), book, chapter);
      } catch (error) {
        // Don't cache failures so a retry can succeed later
        chapterCache.delete(key);
        throw error;
      }
    })();
    chapterCache.set(key, promise);
  }
  return promise;
}

/** Strips formatting, footnote and Strong markup from raw verse text. */
export function cleanVerseText(text: string): string {
  return text
    .replaceAll(/<CM>/gi, "")
    .replaceAll(/<RF>.*?<\\?\/RF>/gi, "")
    .replaceAll(/<\\?\/?[RFrf]+>/g, "")
    .replaceAll(/<(FI|FO)>/gi, "")
    .replaceAll(/<\/(FI|FO)>/gi, "")
    .replaceAll(/<X?W[GH]\d+>/gi, "")
    .trim();
}
